import { useState, useEffect } from 'react';
import { openSignedUrl } from '@/lib/openSignedUrl';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { StatCard, CurrencyDisplay } from '@/components/ui/stat-card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    ArrowLeft, Car, FileText, User, Wrench, Shield, MapPin,
    ExternalLink, Calendar, Gauge, Cog, Hash, Paintbrush, Link2,
    ClipboardCheck, Locate, Activity, ShoppingBag, Phone, Home, Loader2,
    StickyNote, Ship, Anchor, DollarSign, Pencil, AlertTriangle,
} from 'lucide-react';
import { format, parse } from 'date-fns';
import { formatBusinessDate, formatMoney } from '@/lib/format';
import { KIND_LABEL, type Grade, type InspectionKind } from '@/lib/inspections';
import { visitResult } from '@/lib/inspectionResult';
import { ResultBadge } from '@/components/inspections/InspectionChip';
import { driverProfileOf } from '@/lib/driverProfile';
import { cn } from '@/lib/utils';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import { RecordSaleSheet, type SaleInitialData } from '@/components/vehicles/RecordSaleSheet';
import {
    PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip,
    AreaChart, Area, XAxis, YAxis, CartesianGrid,
    RadialBarChart, RadialBar,
} from 'recharts';

// ─── Types ───────────────────────────────────────
// Field names MUST mirror get_vehicle_detail's RETURNS TABLE columns exactly.
// Nullability is hand-maintained: the RPC LEFT JOINs registration/partner/contract/
// procurement, so those columns come back null even though codegen types them non-null.
interface VehicleDetail {
    vehicle_id: number;
    vehicle_make: string;
    vehicle_model: string;
    vehicle_color: string;
    vehicle_vin: string;
    vehicle_manufactured_at: number | null;
    vehicle_mileage: number | null;
    vehicle_engine_size: number | null;
    vehicle_description: string | null;
    vehicle_listing_url: string | null;
    vehicle_created_at: string;
    vehicle_updated_at: string;
    reg_number: string | null;
    reg_type: string | null;
    reg_certificate_url: string | null;
    partner_id: number | null;
    partner_name: string | null;
    purchase_transaction_id: number | null;
    contract_id: number | null;
    contract_status: string | null;
    contract_value: number | null;
    contract_created_at: string | null;
    driver_first_name: string | null;
    driver_last_name: string | null;
    // Procurement fields from vehicle_procurement join
    vehicle_cif: number | null;
    vehicle_est_arrival_at: string | null;
    vehicle_ship_name: string | null;
    vehicle_voyage_no: string | null;
    vehicle_ship_date: string | null;
    vehicle_bl_tracking_no: string | null;
}

interface FinancialSummary {
    purchase_cost: number;
    contract_value: number;
    total_repaid: number;
    total_maintenance_cost: number;
    insurance_count: number;
    insurance_total_value: number;
    repayment_count: number;
    contract_count: number;
    inspection_count: number;
    roadworthy_count: number;
    maintenance_count: number;
    tracker_count: number;
    weekly_instalment: number;
}

interface RepaymentPoint {
    week_start: string;
    amount: number;
    cumulative: number;
}

interface ContractHistory {
    contract_id: number;
    status: string;
    sold_at: number;
    signed_at: string;
    driver_first_name: string;
    driver_last_name: string;
}

interface MaintenanceRecord {
    id: number;
    description: string;
    type: string | null;
    started_at: string | null;
    ended_at: string | null;
    created_at: string;
    transaction_id: number | null;
}

interface RoadworthinessRecord {
    id: number;
    expires_at: string;
    certificate_url: string | null;
    created_at: string;
}

interface InsuranceRecord {
    id: number;
    valued_at: number | null;
    starts_at: string;
    certificate_url: string | null;
    created_at: string;
}

interface InspectionRecord {
    id: number;
    inspection_at: string | null;
    description: string | null;
    created_at: string;
    /** NULL: a legacy, notes-only row. */
    kind: InspectionKind | null;
    vehicle_inspection_lines: Array<{ grade: Grade }> | null;
}

interface TrackerRecord {
    id: number;
    tracker_id: number;
    type: string | null;
    engine_stopped: boolean | null;
    created_at: string;
}

// ─── Helpers ─────────────────────────────────────
function formatCurrency(amount: number) {
    return `GH₵${amount.toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function getStatusBadge(status: string | null) {
    if (!status) return <Badge variant="outline" className="text-muted-foreground">Unassigned</Badge>;
    const map: Record<string, { label: string; className: string }> = {
        IN_PROGRESS: { label: 'Active', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400 hover:bg-emerald-100' },
        COMPLETED: { label: 'Completed', className: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400 hover:bg-blue-100' },
        TERMINATED: { label: 'Terminated', className: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400 hover:bg-red-100' },
    };
    const s = map[status] || { label: status, className: '' };
    return <Badge className={s.className}>{s.label}</Badge>;
}

function getColorValue(color: string): string {
    const trimmed = color.trim();
    if (/^[0-9A-Fa-f]{6}$/.test(trimmed)) return `#${trimmed}`;
    if (trimmed.startsWith('#')) return trimmed;
    return trimmed;
}

// ─── Detail Row Component ────────────────────────
function DetailRow({ icon: Icon, label, value, href, onClick, className }: { icon: any; label: string; value: React.ReactNode; href?: string; onClick?: () => void; className?: string }) {
    return (
        <div className={cn("flex flex-col gap-1.5", className)}>
            <div className="flex items-center gap-2 text-muted-foreground">
                <Icon className="h-3.5 w-3.5" />
                <span className="text-xs font-medium uppercase tracking-wider">{label}</span>
            </div>
            {onClick ? (
                <button onClick={onClick} className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1">
                    {value} <ExternalLink className="h-3 w-3" />
                </button>
            ) : href ? (
                <a href={href} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1">
                    {value} <ExternalLink className="h-3 w-3" />
                </a>
            ) : (
                <div className="text-sm font-medium">{value}</div>
            )}
        </div>
    );
}

// ─── Chart Tooltips ──────────────────────────────
const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
        return (
            <div className="rounded-lg border bg-background px-3 py-2 shadow-md text-sm">
                <p className="font-medium">{payload[0].payload.name || payload[0].name}</p>
                <p className="text-muted-foreground">{formatCurrency(payload[0].value)}</p>
            </div>
        );
    }
    return null;
};

const TimelineTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <div className="rounded-lg border bg-background px-3 py-2 shadow-md text-sm">
                <p className="font-medium text-muted-foreground text-xs">{label}</p>
                <p className="font-medium">Collected: {formatCurrency(payload[0]?.value || 0)}</p>
                {payload[1] && <p className="text-muted-foreground">Expected: {formatCurrency(payload[1]?.value || 0)}</p>}
            </div>
        );
    }
    return null;
};
// ─── Record Sale Sheet (shared component) ───────
// Imported from @/components/vehicles/RecordSaleSheet

// ─── Main Component ──────────────────────────────
export default function VehicleDetail() {
    const { vehicleId } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState('overview');
    const [saleDialogOpen, setSaleDialogOpen] = useState(false);
    const [editSaleOpen, setEditSaleOpen] = useState(false);

    useEffect(() => { window.scrollTo(0, 0); }, []);

    // ── Core vehicle data ─────────────────
    // Deliberately NOT `supabase as any`: the checked cast below makes the generated
    // RPC types reject any field-name drift between this file and get_vehicle_detail.
    const { data: vehicle, isLoading, error, refetch } = useQuery({
        queryKey: ['vehicle-detail', vehicleId],
        queryFn: async () => {
            const { data, error } = await supabase.rpc('get_vehicle_detail', {
                p_vehicle_id: parseInt(vehicleId || '0'),
            });
            if (error) throw error;
            return (data as VehicleDetail[])?.[0] ?? null;
        },
        enabled: !!vehicleId,
        retry: false,
    });

    // ── Financial summary ─────────────────
    const { data: financials } = useQuery({
        queryKey: ['vehicle-financials', vehicleId],
        queryFn: async () => {
            const { data, error } = await supabase.rpc('get_vehicle_financial_summary', {
                p_vehicle_id: parseInt(vehicleId || '0'),
            });
            if (error) throw error;
            return (data as FinancialSummary[])?.[0] ?? null;
        },
        enabled: !!vehicleId,
    });

    // ── Purchase cost per currency ─────────
    // ONE SUM PER CURRENCY. The summary's purchase_cost adds every payment on the car whatever its
    // currency — CIF in USD, tax bill and port charges in GHS — into one figure. The same payments
    // (every vehicle_transactions row of the car) are summed here per currency instead, cedis
    // first. Purchase Cost leads with the first and prints each other currency beside it — a car
    // paid for only in USD reads in dollars, not as GH₵0.00. The cost chart draws the cedis only and
    // lists the rest under it.
    // THIS CAR'S SHARE, not the whole payment: a fan-out transaction (one GRA tax-bill or customs
    // payment split across a container's cars) carries each car's share on allocated_amount, so a
    // row counts `allocated_amount ?? transactions.amount` — the Vehicles list's rule
    // (VehicleValueDisplay) and get_all_vehicle_transactions' COALESCE(vt.allocated_amount, t.amount).
    // A side read: while it loads, or if it fails, Purchase Cost shows an em dash (and says it could
    // not be read), and the cost chart keeps the slices the summary did return.
    const { data: purchaseByCurrency, isError: purchaseFailed } = useQuery({
        queryKey: ['vehicle-purchase-by-currency', vehicleId],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('vehicle_transactions')
                .select('allocated_amount, transactions!inner(amount, currency)')
                .eq('vehicle_id', parseInt(vehicleId || '0'));
            if (error) throw error;
            const totals = new Map<string, number>();
            (data || []).forEach(r => totals.set(r.transactions.currency, (totals.get(r.transactions.currency) || 0) + (r.allocated_amount ?? r.transactions.amount)));
            return Array.from(totals.entries())
                .sort(([a], [b]) => (a === 'GHS' ? -1 : b === 'GHS' ? 1 : 0))
                .map(([currency, amount]) => ({ currency, amount }));
        },
        enabled: !!vehicleId,
    });

    // ── Repayment timeline ────────────────
    const { data: timeline } = useQuery({
        queryKey: ['vehicle-timeline', vehicleId],
        queryFn: async () => {
            const { data, error } = await supabase.rpc('get_vehicle_repayment_timeline', {
                p_vehicle_id: parseInt(vehicleId || '0'),
            });
            if (error) throw error;
            return (data as RepaymentPoint[]) || [];
        },
        enabled: !!vehicleId,
    });

    // ── Contract history ──────────────────
    const { data: contracts, isLoading: contractsLoading } = useQuery({
        queryKey: ['vehicle-contracts', vehicleId],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('contracts')
                .select(`
                    id,
                    sold_at,
                    created_at,
                    driver:drivers!inner(
                        profile:profiles!inner(id, admin_id, first_name, last_name)
                    ),
                    latest_update:contract_updates(status)
                `)
                .eq('vehicle_id', parseInt(vehicleId || '0'))
                .order('created_at', { ascending: false });
            if (error) throw error;
            return (data || []).map((c: any) => {
                // THE DRIVER'S OWN PROFILE, NOT THE FIRST OF THE EMBED. `profiles.driver_id` is
                // unique only WHERE `admin_id IS NULL`, so an admin testing as this driver holds a
                // second row against the same id — see `driverProfileOf`. Both name halves come
                // off the one row it picks.
                const profile = driverProfileOf(c.driver?.profile);
                return {
                    contract_id: c.id,
                    sold_at: c.sold_at,
                    signed_at: c.created_at,
                    driver_first_name: profile?.first_name || '',
                    driver_last_name: profile?.last_name || '',
                    status: c.latest_update?.[c.latest_update.length - 1]?.status || null,
                };
            }) as ContractHistory[];
        },
        enabled: !!vehicleId && activeTab === 'contracts',
    });

    // ── Vehicle sale data ────────────────────
    const { data: vehicleSale } = useQuery({
        queryKey: ['vehicle-sale', vehicleId],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('vehicle_sales')
                .select('id, expected_amount, notes, vehicle_sale_transactions(transaction_id, transactions(amount, currency, transaction_at, description)), buyers(id, name, phone, address)')
                .eq('vehicle_id', parseInt(vehicleId || '0'))
                .maybeSingle();
            if (error) throw error;
            return data;
        },
        enabled: !!vehicleId,
    });

    // ── Maintenance records ────────────────
    const { data: maintenances, isLoading: maintenancesLoading } = useQuery({
        queryKey: ['vehicle-maintenances', vehicleId],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('vehicle_maintenances')
                .select('id, description, type, started_at, ended_at, created_at, transaction_id')
                .eq('vehicle_id', parseInt(vehicleId || '0'))
                .order('created_at', { ascending: false });
            if (error) throw error;
            return data as MaintenanceRecord[];
        },
        enabled: !!vehicleId && activeTab === 'maintenance',
    });

    // ── Roadworthiness ──────────────────────
    const { data: roadworthiness, isLoading: roadworthinessLoading } = useQuery({
        queryKey: ['vehicle-roadworthiness', vehicleId],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('vehicle_roadworthiness')
                .select('id, expires_at, certificate_url, created_at')
                .eq('vehicle_id', parseInt(vehicleId || '0'))
                .order('expires_at', { ascending: false });
            if (error) throw error;
            return data as RoadworthinessRecord[];
        },
        enabled: !!vehicleId && activeTab === 'compliance',
    });

    // ── Insurances ─────────────────────────
    const { data: insurances, isLoading: insurancesLoading } = useQuery({
        queryKey: ['vehicle-insurances', vehicleId],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('insurances')
                .select('id, valued_at, starts_at, certificate_url, created_at')
                .eq('vehicle_id', parseInt(vehicleId || '0'))
                .order('created_at', { ascending: false });
            if (error) throw error;
            return data as InsuranceRecord[];
        },
        enabled: !!vehicleId && activeTab === 'compliance',
    });

    // ── Inspections ─────────────────────────
    const { data: inspections, isLoading: inspectionsLoading } = useQuery({
        queryKey: ['vehicle-inspections', vehicleId],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('vehicle_inspections')
                .select('id, inspection_at, description, created_at, kind, vehicle_inspection_lines(grade)')
                .eq('vehicle_id', parseInt(vehicleId || '0'))
                .order('created_at', { ascending: false });
            if (error) throw error;
            return data as InspectionRecord[];
        },
        enabled: !!vehicleId && activeTab === 'compliance',
    });

    // ── Trackers ────────────────────────────
    const { data: trackers, isLoading: trackersLoading } = useQuery({
        queryKey: ['vehicle-trackers', vehicleId],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('vehicle_trackers')
                .select('id, tracker_id, type, engine_stopped, created_at')
                .eq('vehicle_id', parseInt(vehicleId || '0'))
                .order('created_at', { ascending: false });
            if (error) throw error;
            return data as TrackerRecord[];
        },
        enabled: !!vehicleId && activeTab === 'tracker',
    });

    // ── Record sale mutation ───────────────
    const recordSaleMutation = useMutation({
        mutationFn: async (form: { buyerName: string; buyerPhone: string; buyerAddress: string; transactionIds: number[]; expectedAmount: number; notes: string }) => {
            // Create buyer first
            const { data: buyer, error: buyerError } = await supabase
                .from('buyers')
                .insert({ name: form.buyerName, phone: form.buyerPhone || null, address: form.buyerAddress || null })
                .select('id')
                .single();
            if (buyerError) throw buyerError;

            // Create vehicle sale with expected amount and notes
            const { data: sale, error: saleError } = await supabase
                .from('vehicle_sales')
                .insert({
                    vehicle_id: parseInt(vehicleId || '0'),
                    buyer_id: buyer.id,
                    expected_amount: form.expectedAmount,
                    notes: form.notes || null,
                })
                .select('id')
                .single();
            if (saleError) throw saleError;

            // Link transactions via junction table
            if (form.transactionIds.length > 0) {
                const { error: junctionError } = await supabase
                    .from('vehicle_sale_transactions')
                    .insert(
                        form.transactionIds.map(txnId => ({
                            vehicle_sale_id: sale.id,
                            transaction_id: txnId,
                        }))
                    );
                if (junctionError) throw junctionError;
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['vehicle-sale', vehicleId] });
            queryClient.invalidateQueries({ queryKey: ['vehicle-sales-map'] });
            queryClient.invalidateQueries({ queryKey: ['unlinked-credit-transactions'] });
            setSaleDialogOpen(false);
        },
    });

    // ── Edit sale mutation ────────────────
    const editSaleMutation = useMutation({
        mutationFn: async (form: { buyerName: string; buyerPhone: string; buyerAddress: string; transactionIds: number[]; expectedAmount: number; notes: string }) => {
            if (!vehicleSale) throw new Error('No sale to edit');
            const saleId = (vehicleSale as any).id;
            const buyerId = (vehicleSale as any).buyers?.id;

            // Update buyer
            if (buyerId) {
                const { error: buyerError } = await supabase
                    .from('buyers')
                    .update({ name: form.buyerName, phone: form.buyerPhone || null, address: form.buyerAddress || null })
                    .eq('id', buyerId);
                if (buyerError) throw buyerError;
            }

            // Update vehicle sale
            const { error: saleError } = await supabase
                .from('vehicle_sales')
                .update({ expected_amount: form.expectedAmount, notes: form.notes || null })
                .eq('id', saleId);
            if (saleError) throw saleError;

            // Replace junction rows
            const { error: deleteError } = await supabase
                .from('vehicle_sale_transactions')
                .delete()
                .eq('vehicle_sale_id', saleId);
            if (deleteError) throw deleteError;

            if (form.transactionIds.length > 0) {
                const { error: insertError } = await supabase
                    .from('vehicle_sale_transactions')
                    .insert(
                        form.transactionIds.map(txnId => ({
                            vehicle_sale_id: saleId,
                            transaction_id: txnId,
                        }))
                    );
                if (insertError) throw insertError;
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['vehicle-sale', vehicleId] });
            queryClient.invalidateQueries({ queryKey: ['vehicle-sales-map'] });
            queryClient.invalidateQueries({ queryKey: ['unlinked-credit-transactions'] });
            setEditSaleOpen(false);
        },
    });

    // ── Loading state ────────────────────────
    if (isLoading) {
        return (
            <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
                <Skeleton className="h-6 w-40" />
                <div className="flex gap-4">
                    <Skeleton className="h-16 w-16 rounded-xl" />
                    <div className="space-y-2 flex-1">
                        <Skeleton className="h-8 w-64" />
                        <Skeleton className="h-4 w-40" />
                    </div>
                </div>
                <div className="grid grid-cols-5 gap-4">
                    {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}
                </div>
                <Skeleton className="h-72 w-full rounded-xl" />
            </div>
        );
    }

    // ── Load failure (NOT the same as "not found") ────
    if (error) {
        return (
            <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
                <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/vehicles')} className="mb-4 -ml-2 text-muted-foreground hover:text-foreground">
                    <ArrowLeft className="h-4 w-4 mr-1" /> Vehicles
                </Button>
                <div className="flex flex-col items-center justify-center h-64 text-center text-destructive">
                    <AlertTriangle className="h-12 w-12 mb-4" />
                    <p className="text-lg font-medium">Failed to load vehicle #{vehicleId}</p>
                    {/* PostgREST errors are not always Error instances — read .message off the cast. */}
                    <p className="text-sm text-muted-foreground mt-1 max-w-md break-words">
                        {(error as Error)?.message || String(error)}
                    </p>
                    <Button variant="outline" size="sm" className="mt-4" onClick={() => refetch()}>
                        Try again
                    </Button>
                </div>
            </div>
        );
    }

    // ── Not Found ────────────────────────────
    if (!vehicle) {
        return (
            <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
                <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/vehicles')} className="mb-4 -ml-2 text-muted-foreground hover:text-foreground">
                    <ArrowLeft className="h-4 w-4 mr-1" /> Vehicles
                </Button>
                <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                    <Car className="h-12 w-12 mb-4" />
                    <p className="text-lg">Vehicle not found</p>
                    <p className="text-sm">The vehicle you&apos;re looking for doesn&apos;t exist or has been removed.</p>
                </div>
            </div>
        );
    }

    // ── Derived data ─────────────────────────
    const colorHex = getColorValue(vehicle.vehicle_color);
    const outstanding = financials ? Math.max(0, financials.contract_value - financials.total_repaid) : 0;
    const purchaseGhs = purchaseByCurrency?.find(g => g.currency === 'GHS')?.amount ?? 0;
    const progressPercent = financials && financials.contract_value > 0
        ? Math.min(100, (financials.total_repaid / financials.contract_value) * 100)
        : 0;

    // Chart: Radial progress
    const radialData = [
        { name: 'Repaid', value: Math.round(progressPercent), fill: '#10b981' },
    ];

    // Chart: Cost breakdown donut. Insurance and Maintenance come from the summary alone; the
    // Purchase slice is added only once its own read is in, and until then the legend shows it as
    // unknown and the total is not printed — a total without the purchase is not the total.
    const costData = financials ? [
        ...(purchaseByCurrency ? [{ name: 'Purchase', value: purchaseGhs, color: '#0f172a' }] : []),
        { name: 'Insurance', value: financials.insurance_total_value, color: '#64748b' },
        { name: 'Maintenance', value: financials.total_maintenance_cost, color: '#cbd5e1' },
    ].filter(d => d.value > 0) : [];
    const purchaseUnknown = !!financials && !purchaseByCurrency;
    const totalCosts = costData.reduce((s, d) => s + d.value, 0);

    // Chart: Timeline with expected line
    const timelineData = (timeline || []).map((p, i) => ({
        // week_start is a DATE; `new Date('2026-08-10')` is UTC midnight, so every point on this
        // timeline labelled a day early at negative offsets. Parse to local midnight instead.
        name: format(parse(p.week_start.substring(0, 10), 'yyyy-MM-dd', new Date()), 'MMM d'),
        collected: p.cumulative,
        expected: financials ? financials.weekly_instalment * (i + 1) : 0,
    }));

    // Activity counts
    const activityCounts = financials ? [
        { icon: FileText, label: 'Contracts', count: financials.contract_count },
        { icon: Activity, label: 'Repayments', count: financials.repayment_count },
        { icon: Shield, label: 'Insurance', count: financials.insurance_count },
        { icon: Wrench, label: 'Maintenance', count: financials.maintenance_count },
        { icon: ClipboardCheck, label: 'Inspections', count: financials.inspection_count },
        { icon: Locate, label: 'Trackers', count: financials.tracker_count },
    ] : [];

    return (
        <>
        <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6 pb-12">
            {/* Back button */}
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="-ml-2 text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>

            {/* ════════════════════════════════════════════ */}
            {/* Hero Header                                 */}
            {/* ════════════════════════════════════════════ */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div
                        className="h-16 w-16 rounded-xl border-2 border-border/60 shadow-sm flex-shrink-0"
                        style={{ backgroundColor: colorHex || '#e5e7eb' }}
                    />
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
                            {vehicle.vehicle_make} {vehicle.vehicle_model}
                            {vehicle.vehicle_manufactured_at ? <span className="text-muted-foreground font-normal text-xl sm:text-2xl ml-2">({vehicle.vehicle_manufactured_at})</span> : null}
                        </h1>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                            {vehicle.reg_number && (
                                <Badge variant="outline" className="font-mono text-xs">{vehicle.reg_number}</Badge>
                            )}
                            <code className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{vehicle.vehicle_vin}</code>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {getStatusBadge(vehicle.contract_status)}
                    {vehicle.contract_id && (
                        <Button variant="outline" size="sm" onClick={() => navigate(`/dashboard/contracts/${vehicle.contract_id}`)}>
                            <FileText className="h-4 w-4 mr-1" /> View Contract
                        </Button>
                    )}
                    {!vehicleSale && (
                        <Button variant="outline" size="sm" onClick={() => setSaleDialogOpen(true)}>
                            <ShoppingBag className="h-4 w-4 mr-1" /> Record Sale
                        </Button>
                    )}
                </div>
            </div>

            {/* Assignment pills */}
            <div className="flex flex-wrap items-center gap-4 text-sm">
                {vehicle.driver_first_name && (
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                        <User className="h-3.5 w-3.5" />
                        <span>{vehicle.driver_first_name} {vehicle.driver_last_name}</span>
                    </div>
                )}
                {vehicle.partner_name && (
                    <button
                        onClick={() => navigate(`/dashboard/partners/${vehicle.partner_id}`)}
                        className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
                    >
                        <User className="h-3.5 w-3.5" />
                        <span>{vehicle.partner_name}</span>
                    </button>
                )}
            </div>

            {/* ════════════════════════════════════════════ */}
            {/* Financial Stats Row                         */}
            {/* ════════════════════════════════════════════ */}
            <div className="grid gap-4 grid-cols-2 md:grid-cols-5">
                <StatCard
                    title="Purchase Cost"
                    value={purchaseByCurrency ? <CurrencyDisplay amount={purchaseByCurrency[0]?.amount ?? 0} currency={purchaseByCurrency[0]?.currency} /> : '—'}
                    subtitle={purchaseFailed ? 'Could not be read' : purchaseByCurrency?.slice(1).map(o => `and ${formatMoney(o.amount, o.currency)}`).join(' · ') || undefined}
                    description="What was paid for this vehicle"
                />
                <StatCard
                    title="Contract Value"
                    value={financials ? <CurrencyDisplay amount={financials.contract_value} /> : '—'}
                    description="What the vehicle was sold for under contract"
                />
                <StatCard
                    title="Total Repaid"
                    value={financials ? <CurrencyDisplay amount={financials.total_repaid} /> : '—'}
                    description="Total payments collected towards contract"
                />
                <StatCard
                    title="Outstanding"
                    value={financials ? <CurrencyDisplay amount={outstanding} /> : '—'}
                    description="Remaining balance to be paid off"
                />
                <StatCard
                    title="Weekly Instalment"
                    value={financials ? <CurrencyDisplay amount={financials.weekly_instalment} /> : '—'}
                    description="Current weekly payment amount"
                />
            </div>

            {/* ════════════════════════════════════════════ */}
            {/* Charts Row                                  */}
            {/* ════════════════════════════════════════════ */}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {/* ── Repayment Progress Radial ── */}
                <Card className="border border-border/40">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-medium">Repayment Progress</CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-col items-center justify-center">
                        <div className="relative w-[200px] h-[200px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <RadialBarChart
                                    cx="50%" cy="50%"
                                    innerRadius="70%"
                                    outerRadius="100%"
                                    barSize={14}
                                    data={radialData}
                                    startAngle={90}
                                    endAngle={-270}
                                >
                                    <RadialBar
                                        dataKey="value"
                                        cornerRadius={10}
                                        background={{ fill: 'hsl(var(--muted))' }}
                                    />
                                </RadialBarChart>
                            </ResponsiveContainer>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className="text-3xl font-bold">{Math.round(progressPercent)}%</span>
                                <span className="text-xs text-muted-foreground">Repaid</span>
                            </div>
                        </div>
                        <div className="flex gap-6 mt-4 text-sm">
                            <div className="text-center">
                                <p className="font-semibold">{formatCurrency(financials?.total_repaid || 0)}</p>
                                <p className="text-xs text-muted-foreground">Collected</p>
                            </div>
                            <div className="text-center">
                                <p className="font-semibold">{formatCurrency(outstanding)}</p>
                                <p className="text-xs text-muted-foreground">Remaining</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* ── Cumulative Repayments Area Chart ── */}
                <Card className="border border-border/40 lg:col-span-1">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-medium">Cumulative Collections</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {timelineData.length > 0 ? (
                            <div className="h-[240px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={timelineData} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="collectedGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                                                <stop offset="95%" stopColor="#10b981" stopOpacity={0.02} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                                        <XAxis
                                            dataKey="name"
                                            tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <YAxis
                                            tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                                            axisLine={false}
                                            tickLine={false}
                                            tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v}
                                        />
                                        <RTooltip content={<TimelineTooltip />} />
                                        <Area
                                            type="monotone"
                                            dataKey="collected"
                                            stroke="#10b981"
                                            strokeWidth={2}
                                            fill="url(#collectedGradient)"
                                            name="Collected"
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="expected"
                                            stroke="hsl(var(--muted-foreground))"
                                            strokeWidth={1.5}
                                            strokeDasharray="5 5"
                                            fill="transparent"
                                            name="Expected"
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        ) : (
                            <div className="h-[240px] flex items-center justify-center text-sm text-muted-foreground">
                                No repayment data yet
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* ── Cost Breakdown Donut ── */}
                <Card className="border border-border/40">
                    <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-base font-medium">Cost Breakdown</CardTitle>
                            <span className="text-sm font-semibold">{purchaseUnknown ? '—' : formatCurrency(totalCosts)}</span>
                        </div>
                    </CardHeader>
                    <CardContent className="flex flex-col items-center gap-4">
                        {costData.length > 0 || purchaseUnknown ? (
                            <>
                                {costData.length > 0 && (
                                <div className="relative w-[180px] h-[180px]">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={costData}
                                                cx="50%" cy="50%"
                                                innerRadius={55}
                                                outerRadius={80}
                                                paddingAngle={3}
                                                dataKey="value"
                                                stroke="none"
                                            >
                                                {costData.map((entry, i) => (
                                                    <Cell key={i} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <RTooltip content={<CustomTooltip />} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                                )}
                                <div className="w-full space-y-2">
                                    {purchaseUnknown && (
                                        <div className="flex items-center justify-between text-sm">
                                            <div className="flex items-center gap-2">
                                                <div className="h-3 w-3 rounded-sm" style={{ backgroundColor: '#0f172a' }} />
                                                <span className="text-muted-foreground">{purchaseFailed ? 'Purchase (could not be read)' : 'Purchase'}</span>
                                            </div>
                                            <span className="font-medium">—</span>
                                        </div>
                                    )}
                                    {costData.map((item, i) => (
                                        <div key={i} className="flex items-center justify-between text-sm">
                                            <div className="flex items-center gap-2">
                                                <div className="h-3 w-3 rounded-sm" style={{ backgroundColor: item.color }} />
                                                <span className="text-muted-foreground">{item.name}</span>
                                            </div>
                                            <span className="font-medium">{formatCurrency(item.value)}</span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : purchaseByCurrency?.some(o => o.currency !== 'GHS') ? null : (
                            // Not where the purchase is in another currency: it is listed just below.
                            <div className="h-[240px] flex items-center justify-center text-sm text-muted-foreground">
                                No cost data available
                            </div>
                        )}
                        {purchaseByCurrency?.filter(o => o.currency !== 'GHS').map(o => (
                            <p key={o.currency} className="w-full text-xs text-muted-foreground">
                                Purchase in {o.currency}, not in the chart: {formatMoney(o.amount, o.currency)}
                            </p>
                        ))}
                    </CardContent>
                </Card>
            </div>

            {/* ════════════════════════════════════════════ */}
            {/* Activity Counts                             */}
            {/* ════════════════════════════════════════════ */}
            <div className="flex flex-wrap gap-3">
                {activityCounts.map(({ icon: Icon, label, count }) => (
                    <div key={label} className="flex items-center gap-1.5 rounded-full border border-border/60 bg-muted/30 px-3 py-1.5 text-sm">
                        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="font-medium">{count}</span>
                        <span className="text-muted-foreground">{label}</span>
                    </div>
                ))}
            </div>

            {/* ════════════════════════════════════════════ */}
            {/* Sale Details (shown when vehicle is sold)    */}
            {/* ════════════════════════════════════════════ */}
            {vehicleSale && (() => {
                const txns = ((vehicleSale as any).vehicle_sale_transactions || []) as { transaction_id: number; transactions: { amount: number; currency: string; transaction_at: string; description: string | null } }[];
                const totalReceived = txns.reduce((sum, vst) => sum + (vst.transactions?.amount || 0), 0);
                const expectedAmount = (vehicleSale as any).expected_amount ?? 0;
                const outstanding = expectedAmount - totalReceived;
                const saleNotes = (vehicleSale as any).notes as string | null;

                return (
                    <Card className="border border-border/40 bg-slate-50/50 dark:bg-slate-900/20">
                        <CardHeader className="pb-3">
                            <div className="flex items-center gap-2">
                                <ShoppingBag className="h-4 w-4 text-muted-foreground" />
                                <CardTitle className="text-base">Sale Details</CardTitle>
                                <Badge variant="outline" className="ml-auto font-normal">Sold</Badge>
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditSaleOpen(true)}>
                                    <Pencil className="h-3.5 w-3.5" />
                                    <span className="sr-only">Edit Sale</span>
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Buyer info row */}
                            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-6">
                                <DetailRow icon={User} label="Buyer" value={(vehicleSale as any).buyers?.name || '—'} />
                                <DetailRow icon={Phone} label="Phone" value={(vehicleSale as any).buyers?.phone || '—'} />
                                <DetailRow icon={Home} label="Address" value={(vehicleSale as any).buyers?.address || '—'} />
                            </div>

                            {/* Financial summary */}
                            <div className="grid sm:grid-cols-3 gap-4">
                                <div className="rounded-lg border bg-background p-4">
                                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Expected Amount</p>
                                    <p className="text-lg font-bold">{formatCurrency(expectedAmount)}</p>
                                </div>
                                <div className="rounded-lg border bg-background p-4">
                                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Total Received</p>
                                    <p className="text-lg font-bold text-green-600 dark:text-green-400">{formatCurrency(totalReceived)}</p>
                                </div>
                                <div className="rounded-lg border bg-background p-4">
                                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Outstanding</p>
                                    <p className={`text-lg font-bold ${outstanding > 0 ? 'text-red-500' : 'text-green-600 dark:text-green-400'}`}>
                                        {formatCurrency(Math.abs(outstanding))}
                                        {outstanding <= 0 && ' ✓'}
                                    </p>
                                </div>
                            </div>

                            {/* Linked transactions list */}
                            {txns.length > 0 && (
                                <div className="space-y-2">
                                    <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Linked Transactions ({txns.length})</p>
                                    <div className="divide-y rounded-lg border bg-background">
                                        {txns.map((vst, i) => (
                                            <div key={i} className="flex items-center justify-between px-4 py-3">
                                                <div className="min-w-0">
                                                    <p className="text-sm font-medium">
                                                        {vst.transactions?.transaction_at
                                                            ? formatBusinessDate(vst.transactions.transaction_at)
                                                            : '—'}
                                                    </p>
                                                    {vst.transactions?.description && (
                                                        <p className="text-xs text-muted-foreground truncate">{vst.transactions.description}</p>
                                                    )}
                                                </div>
                                                <span className="font-mono font-medium text-sm text-green-600 dark:text-green-400">
                                                    {vst.transactions?.amount ? formatCurrency(vst.transactions.amount) : '—'}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Notes */}
                            {saleNotes && (
                                <DetailRow icon={StickyNote} label="Notes" value={saleNotes} />
                            )}
                        </CardContent>
                    </Card>
                );
            })()}

            {/* ════════════════════════════════════════════ */}
            {/* Tabs                                        */}
            {/* ════════════════════════════════════════════ */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                <TabsList>
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="contracts">Contracts</TabsTrigger>
                    <TabsTrigger value="maintenance">Maintenance</TabsTrigger>
                    <TabsTrigger value="compliance">Compliance</TabsTrigger>
                    <TabsTrigger value="tracker">Tracker</TabsTrigger>
                </TabsList>

                {/* ─── Overview Tab ──────────────────────── */}
                <TabsContent value="overview" className="space-y-6">
                    <div className="grid md:grid-cols-3 gap-6">
                        {/* Vehicle Specifications */}
                        <Card className="md:col-span-2 border border-border/40">
                            <CardHeader>
                                <CardTitle className="text-base">Vehicle Specifications</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
                                    <DetailRow icon={Car} label="Make" value={vehicle.vehicle_make} />
                                    <DetailRow icon={Car} label="Model" value={vehicle.vehicle_model} />
                                    <DetailRow icon={Calendar} label="Year" value={vehicle.vehicle_manufactured_at || '—'} />
                                    <DetailRow icon={Paintbrush} label="Color" value={
                                        <div className="flex items-center gap-2">
                                            <div className="h-4 w-4 rounded-full border shadow-sm" style={{ backgroundColor: colorHex }} />
                                            {vehicle.vehicle_color}
                                        </div>
                                    } />
                                    <DetailRow icon={Hash} label="VIN" value={vehicle.vehicle_vin} />
                                    <DetailRow icon={Gauge} label="Mileage" value={vehicle.vehicle_mileage ? `${Number(vehicle.vehicle_mileage).toLocaleString()} km` : '—'} />
                                    <DetailRow icon={Cog} label="Engine Size" value={vehicle.vehicle_engine_size ? `${vehicle.vehicle_engine_size}cc` : '—'} />

                                    {vehicle.vehicle_description && (
                                        <div className="sm:col-span-2 pt-4 border-t border-border/50">
                                            <DetailRow icon={FileText} label="Description / Cost" value={vehicle.vehicle_description} />
                                        </div>
                                    )}

                                    {vehicle.vehicle_listing_url && (
                                        <div className="sm:col-span-2 pt-2">
                                            <DetailRow icon={Link2} label="Listing URL" value="View listing" href={vehicle.vehicle_listing_url} />
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Side Column */}
                        <div className="space-y-6">
                            {/* Procurement Summary */}
                            {(vehicle.vehicle_cif != null || vehicle.vehicle_ship_name || vehicle.vehicle_bl_tracking_no || vehicle.vehicle_est_arrival_at) && (
                                <Card className="border border-border/40">
                                    <CardHeader>
                                        <CardTitle className="text-base">Procurement</CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                        <div className="grid grid-cols-1 gap-6">
                                            {vehicle.vehicle_cif != null && (
                                                <DetailRow icon={DollarSign} label="CIF" value={`$${Number(vehicle.vehicle_cif).toLocaleString()}`} />
                                            )}
                                            {vehicle.vehicle_est_arrival_at && (
                                                <DetailRow icon={Calendar} label="Est. Arrival" value={format(parse(vehicle.vehicle_est_arrival_at.substring(0, 10), 'yyyy-MM-dd', new Date()), 'MMM d, yyyy')} />
                                            )}
                                            {vehicle.vehicle_ship_name && (
                                                <DetailRow icon={Ship} label="Ship" value={vehicle.vehicle_ship_name} />
                                            )}
                                            {vehicle.vehicle_voyage_no && (
                                                <DetailRow icon={Anchor} label="Voyage No" value={vehicle.vehicle_voyage_no} />
                                            )}
                                            {vehicle.vehicle_ship_date && (
                                                <DetailRow icon={Calendar} label="Ship Date" value={format(parse(vehicle.vehicle_ship_date.substring(0, 10), 'yyyy-MM-dd', new Date()), 'MMM d, yyyy')} />
                                            )}
                                            {vehicle.vehicle_bl_tracking_no && (
                                                <DetailRow icon={Hash} label="BL Tracking No" value={vehicle.vehicle_bl_tracking_no} />
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                            <Card className="border border-border/40">
                                <CardHeader>
                                    <CardTitle className="text-base">Registration</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    {vehicle.reg_number ? (
                                        <>
                                            <div className="p-3 bg-muted/30 rounded-lg border border-border/50 text-center">
                                                <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Registration Number</div>
                                                <div className="text-xl font-mono font-medium">{vehicle.reg_number}</div>
                                            </div>
                                            <div className="grid grid-cols-1 gap-6">
                                                <DetailRow icon={FileText} label="Type" value={vehicle.reg_type || '—'} />
                                                {vehicle.reg_certificate_url && (
                                                    <DetailRow icon={ExternalLink} label="Certificate" value="View certificate" onClick={() => openSignedUrl(vehicle.reg_certificate_url!)} />
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="py-8 text-center text-muted-foreground text-sm">
                                            No registration on file
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            <Card className="border border-border/40">
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-sm font-medium text-muted-foreground">Metadata</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <DetailRow icon={Calendar} label="Added to Fleet" value={format(new Date(vehicle.vehicle_created_at), 'MMM d, yyyy h:mm a')} />
                                    <DetailRow icon={Calendar} label="Last Updated" value={format(new Date(vehicle.vehicle_updated_at), 'MMM d, yyyy h:mm a')} />
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </TabsContent>

                {/* ─── Contract History Tab ──────────────── */}
                <TabsContent value="contracts">
                    <Card className="border border-border/40">
                        <CardHeader>
                            <CardTitle className="text-base">Contract History</CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                            {contractsLoading ? (
                                <Skeleton className="h-48 w-full" />
                            ) : contracts && contracts.length > 0 ? (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Contract</TableHead>
                                            <TableHead>Driver</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Value</TableHead>
                                            <TableHead>Signed</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {contracts.map((c) => (
                                            <TableRow
                                                key={c.contract_id}
                                                className="cursor-pointer hover:bg-muted/50"
                                                onClick={() => navigate(`/dashboard/contracts/${c.contract_id}`)}
                                            >
                                                <TableCell className="font-mono text-sm">#{c.contract_id}</TableCell>
                                                <TableCell>{c.driver_first_name} {c.driver_last_name}</TableCell>
                                                <TableCell>{getStatusBadge(c.status)}</TableCell>
                                                <TableCell>{formatCurrency(c.sold_at)}</TableCell>
                                                <TableCell className="text-muted-foreground">
                                                    {format(new Date(c.signed_at), 'MMM d, yyyy')}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <p className="text-center text-muted-foreground py-8 text-sm">No contract history for this vehicle</p>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ─── Maintenance Tab ───────────────────── */}
                <TabsContent value="maintenance">
                    <Card className="border border-border/40">
                        <CardHeader>
                            <CardTitle className="text-base">Maintenance Records</CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                            {maintenancesLoading ? (
                                <Skeleton className="h-48 w-full" />
                            ) : maintenances && maintenances.length > 0 ? (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Description</TableHead>
                                            <TableHead>Type</TableHead>
                                            <TableHead>Period</TableHead>
                                            <TableHead>Added</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {maintenances.map((m) => (
                                            <TableRow key={m.id}>
                                                <TableCell className="font-medium">{m.description}</TableCell>
                                                <TableCell>
                                                    {m.type ? <Badge variant="outline">{m.type}</Badge> : '—'}
                                                </TableCell>
                                                <TableCell className="text-sm text-muted-foreground">
                                                    {m.started_at ? format(new Date(m.started_at), 'MMM d, yyyy') : '—'}
                                                    {m.ended_at ? ` — ${format(new Date(m.ended_at), 'MMM d, yyyy')}` : ''}
                                                </TableCell>
                                                <TableCell className="text-muted-foreground">
                                                    {format(new Date(m.created_at), 'MMM d, yyyy')}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <p className="text-center text-muted-foreground py-8 text-sm">No maintenance records</p>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ─── Compliance Tab ────────────────────── */}
                <TabsContent value="compliance">
                    <div className="space-y-6">
                        {/* Roadworthiness */}
                        <Card className="border border-border/40">
                            <CardHeader>
                                <CardTitle className="text-base">Roadworthiness Certificates</CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                {roadworthinessLoading ? (
                                    <Skeleton className="h-32 w-full" />
                                ) : roadworthiness && roadworthiness.length > 0 ? (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Expiry Date</TableHead>
                                                <TableHead>Certificate</TableHead>
                                                <TableHead>Added</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {roadworthiness.map((r) => (
                                                <TableRow key={r.id}>
                                                    <TableCell className="font-medium">
                                                        {format(new Date(r.expires_at), 'MMM d, yyyy')}
                                                    </TableCell>
                                                    <TableCell>
                                                        {r.certificate_url ? (
                                                            <button onClick={() => openSignedUrl(r.certificate_url!)} className="text-primary hover:underline inline-flex items-center gap-1 text-sm">
                                                                View <ExternalLink className="h-3 w-3" />
                                                            </button>
                                                        ) : (
                                                            <span className="text-muted-foreground">—</span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-muted-foreground">
                                                        {format(new Date(r.created_at), 'MMM d, yyyy')}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <p className="text-center text-muted-foreground py-6 text-sm">No roadworthiness certificates</p>
                                )}
                            </CardContent>
                        </Card>

                        {/* Insurance */}
                        <Card className="border border-border/40">
                            <CardHeader>
                                <CardTitle className="text-base">Insurance Policies</CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                {insurancesLoading ? (
                                    <Skeleton className="h-32 w-full" />
                                ) : insurances && insurances.length > 0 ? (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Start Date</TableHead>
                                                <TableHead>Value</TableHead>
                                                <TableHead>Certificate</TableHead>
                                                <TableHead>Added</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {insurances.map((ins) => (
                                                <TableRow key={ins.id}>
                                                    <TableCell className="text-sm">
                                                        {format(new Date(ins.starts_at), 'MMM d, yyyy')}
                                                    </TableCell>
                                                    <TableCell>{ins.valued_at ? formatCurrency(ins.valued_at) : '—'}</TableCell>
                                                    <TableCell>
                                                        {ins.certificate_url ? (
                                                            <button onClick={() => openSignedUrl(ins.certificate_url!)} className="text-primary hover:underline text-sm inline-flex items-center gap-1">
                                                                View <ExternalLink className="h-3 w-3" />
                                                            </button>
                                                        ) : '—'}
                                                    </TableCell>
                                                    <TableCell className="text-muted-foreground">
                                                        {format(new Date(ins.created_at), 'MMM d, yyyy')}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <p className="text-center text-muted-foreground py-6 text-sm">No insurance policies</p>
                                )}
                            </CardContent>
                        </Card>

                        {/* Inspections */}
                        <Card className="border border-border/40">
                            <CardHeader>
                                <CardTitle className="text-base">Inspections</CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                {inspectionsLoading ? (
                                    <Skeleton className="h-32 w-full" />
                                ) : inspections && inspections.length > 0 ? (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Inspection Date</TableHead>
                                                <TableHead>Kind</TableHead>
                                                <TableHead>Result</TableHead>
                                                <TableHead>Remarks</TableHead>
                                                <TableHead>Added</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {inspections.map((insp) => (
                                                <TableRow key={insp.id}>
                                                    <TableCell className="text-muted-foreground">
                                                        {insp.inspection_at ? formatBusinessDate(insp.inspection_at) : '—'}
                                                    </TableCell>
                                                    <TableCell className="text-sm">{insp.kind ? KIND_LABEL[insp.kind] : '—'}</TableCell>
                                                    <TableCell>
                                                        <ResultBadge result={visitResult(insp.kind, (insp.vehicle_inspection_lines ?? []).map((l) => l.grade))} />
                                                    </TableCell>
                                                    <TableCell className="text-sm">{insp.description || '—'}</TableCell>
                                                    <TableCell className="text-muted-foreground">
                                                        {formatBusinessDate(insp.created_at)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <p className="text-center text-muted-foreground py-6 text-sm">No inspections</p>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* ─── Tracker Tab ───────────────────────── */}
                <TabsContent value="tracker">
                    <Card className="border border-border/40">
                        <CardHeader>
                            <CardTitle className="text-base">GPS Trackers</CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                            {trackersLoading ? (
                                <Skeleton className="h-48 w-full" />
                            ) : trackers && trackers.length > 0 ? (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Tracker ID</TableHead>
                                            <TableHead>Type</TableHead>
                                            <TableHead>Engine Stopped</TableHead>
                                            <TableHead>Added</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {trackers.map((t) => (
                                            <TableRow key={t.id}>
                                                <TableCell className="font-mono text-sm">#{t.tracker_id}</TableCell>
                                                <TableCell>{t.type || '—'}</TableCell>
                                                <TableCell>
                                                    <Badge variant={t.engine_stopped ? 'destructive' : 'outline'}>
                                                        {t.engine_stopped ? 'Yes' : 'No'}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-muted-foreground">
                                                    {format(new Date(t.created_at), 'MMM d, yyyy')}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <p className="text-center text-muted-foreground py-8 text-sm">No GPS trackers installed</p>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>

            <RecordSaleSheet
                open={saleDialogOpen}
                onOpenChange={setSaleDialogOpen}
                onSubmit={(form) => recordSaleMutation.mutate(form)}
                isPending={recordSaleMutation.isPending}
            />

            {/* Edit Sale Sheet */}
            {vehicleSale && (
                <RecordSaleSheet
                    open={editSaleOpen}
                    onOpenChange={setEditSaleOpen}
                    onSubmit={(form) => editSaleMutation.mutate(form)}
                    isPending={editSaleMutation.isPending}
                    initialData={{
                        saleId: (vehicleSale as any).id,
                        buyerId: (vehicleSale as any).buyers?.id ?? 0,
                        buyerName: (vehicleSale as any).buyers?.name || '',
                        buyerPhone: (vehicleSale as any).buyers?.phone || '',
                        buyerAddress: (vehicleSale as any).buyers?.address || '',
                        expectedAmount: (vehicleSale as any).expected_amount ?? 0,
                        notes: (vehicleSale as any).notes || '',
                        transactions: ((vehicleSale as any).vehicle_sale_transactions || []).map((vst: any) => ({
                            id: vst.transactions?.id ?? vst.transaction_id,
                            amount: vst.transactions?.amount || 0,
                            transaction_at: vst.transactions?.transaction_at || '',
                            description: vst.transactions?.description || null,
                            type: 'CREDIT',
                        })),
                    }}
                />
            )}
        </>
    );
}
